import React, { useState, useMemo, useEffect } from "react";
import { Mail, Shield, Sparkles, Clock, HeartHandshake, ArrowRight, Leaf, ShieldCheck, MessageSquareText, Inbox, ChevronDown, Check, Flame, Send, Users, ArrowRightLeft, Anchor } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const RECIPIENT = "compose_app@verbalens.com";

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const HeroFlowAnimation = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep((s) => (s + 1) % 4);
    }, 2400);
    return () => clearInterval(interval);
  }, []);

  const visible = (i) => step >= i;
  const labels = [
    "Stap 1 · Inkomend bericht",
    "Stap 2 · Heldere samenvatting",
    "Stap 3 · Rustig antwoord",
    "Klaar om te versturen",
  ];

  return (
    <div className="relative mx-auto max-w-[340px] sm:max-w-[380px]" data-testid="hero-flow-animation">
      {/* Ambient glow */}
      <div
        className="absolute -inset-10 bg-gradient-to-tr from-compose-surface via-compose-sage/15 to-transparent rounded-[3rem] -z-10 blur-3xl opacity-80"
        aria-hidden="true"
      />
      {/* Floating accent dots */}
      <span className="absolute -top-4 -right-3 w-3 h-3 rounded-full bg-compose-terracotta/70 animate-pulse" aria-hidden="true" />
      <span className="absolute top-1/3 -left-5 w-2 h-2 rounded-full bg-compose-sage/60" aria-hidden="true" />
      <span className="absolute -bottom-2 right-10 w-2.5 h-2.5 rounded-full bg-compose-dusty/60" aria-hidden="true" />

      {/* Phone */}
      <div className="relative bg-compose-ink rounded-[2.75rem] p-3 shadow-glow">
        {/* Notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-compose-ink rounded-b-2xl z-20 flex items-center justify-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-compose-inkSoft/40" />
          <span className="w-7 h-1 rounded-full bg-compose-inkSoft/25" />
        </div>

        {/* Screen */}
        <div className="bg-compose-bg rounded-[2.25rem] overflow-hidden">
          {/* Status bar */}
          <div className="flex items-center justify-between px-7 pt-5 pb-2 text-[10.5px] font-semibold text-compose-ink">
            <span>9:41</span>
            <div className="flex items-center gap-1 opacity-70">
              <svg width="14" height="9" viewBox="0 0 14 9" fill="none" aria-hidden="true">
                <rect x="0" y="6" width="2.5" height="3" rx="0.5" fill="currentColor" />
                <rect x="3.5" y="4" width="2.5" height="5" rx="0.5" fill="currentColor" />
                <rect x="7" y="2" width="2.5" height="7" rx="0.5" fill="currentColor" />
                <rect x="10.5" y="0" width="2.5" height="9" rx="0.5" fill="currentColor" />
              </svg>
              <svg width="14" height="9" viewBox="0 0 14 9" fill="none" aria-hidden="true">
                <path d="M7 8.5C9.2 8.5 11.2 7.7 12.7 6.3L7 0.5L1.3 6.3C2.8 7.7 4.8 8.5 7 8.5Z" fill="currentColor" />
              </svg>
              <svg width="22" height="11" viewBox="0 0 22 11" fill="none" aria-hidden="true">
                <rect x="0.5" y="0.5" width="18" height="10" rx="2.5" stroke="currentColor" fill="none" />
                <rect x="2" y="2" width="13" height="7" rx="1" fill="currentColor" />
                <rect x="19.5" y="3.5" width="1.5" height="4" rx="0.5" fill="currentColor" />
              </svg>
            </div>
          </div>

          {/* App header */}
          <div className="px-5 pt-3 pb-4 flex items-center justify-between border-b border-compose-border/50">
            <div className="flex items-center gap-2.5">
              <span className="w-9 h-9 rounded-full bg-compose-ink text-compose-bg flex items-center justify-center">
                <Leaf size={17} strokeWidth={1.5} />
              </span>
              <div>
                <p className="font-serif text-[17px] text-compose-ink leading-none">Compose</p>
                <p className="text-[10px] text-compose-inkSoft mt-0.5">Inbox · 1 nieuw</p>
              </div>
            </div>
            {/* Step progress bar */}
            <div className="flex items-center gap-1" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className={`h-1 rounded-full transition-all duration-500 ${
                    step > i || step === 3
                      ? "bg-compose-sage w-5"
                      : step === i
                      ? "bg-compose-sage/70 w-3"
                      : "bg-compose-border w-1.5"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Card stack */}
          <div className="px-4 py-4 space-y-3 min-h-[360px] sm:min-h-[380px]">
            {/* Card 1 — Inkomend */}
            <div
              className={`transition-all duration-700 ease-out ${
                visible(0) ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
              }`}
            >
              <div className="rounded-2xl border border-compose-terracotta/30 bg-compose-terracotta/5 p-3.5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-compose-terracotta/25 text-compose-terracotta text-[10px] font-semibold flex items-center justify-center">M</span>
                    <p className="text-[11.5px] font-medium text-compose-ink">Mark</p>
                  </div>
                  <span className="text-[9px] tracking-[0.18em] uppercase text-compose-terracotta">Geladen</span>
                </div>
                <p className="text-[11.5px] font-medium text-compose-ink mb-1">Schoolvakantie planning</p>
                <p className="text-[11px] leading-relaxed text-compose-inkSoft italic">
                  &ldquo;Ik snap niet waarom je dit altijd zo moeilijk maakt...&rdquo;
                </p>
              </div>
            </div>

            {/* Card 2 — Samenvatting */}
            <div
              className={`transition-all duration-700 ease-out ${
                visible(1) ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
              }`}
            >
              <div className="rounded-2xl border border-compose-border bg-white p-3.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles size={12} strokeWidth={1.5} className="text-compose-sage" />
                  <span className="text-[9px] tracking-[0.18em] uppercase text-compose-sage font-medium">
                    Samenvatting
                  </span>
                </div>
                <p className="text-[11.5px] leading-relaxed text-compose-ink">
                  Mark wil de vakantieweken bespreken en vraagt om jouw input voor een goede verdeling.
                </p>
              </div>
            </div>

            {/* Card 3 — Voorgesteld antwoord */}
            <div
              className={`transition-all duration-700 ease-out ${
                visible(2) ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
              }`}
            >
              <div className="rounded-2xl border border-compose-sage/40 bg-compose-sage/10 p-3.5">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <MessageSquareText size={12} strokeWidth={1.5} className="text-compose-sage" />
                  <span className="text-[9px] tracking-[0.18em] uppercase text-compose-sage font-medium">
                    Voorgesteld antwoord
                  </span>
                </div>
                <p className="text-[11.5px] leading-relaxed text-compose-ink">
                  Dank je voor je bericht. Laten we deze week even afstemmen om samen tot een goede verdeling te
                  komen.
                </p>
                <div className="mt-2.5 flex gap-1.5">
                  <span className="text-[10px] px-2.5 py-1 rounded-full bg-compose-ink text-compose-bg font-medium">
                    Verstuur
                  </span>
                  <span className="text-[10px] px-2.5 py-1 rounded-full border border-compose-border text-compose-ink">
                    Aanpassen
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Step label */}
          <div className="px-5 pb-4 pt-2 border-t border-compose-border/50 flex items-center justify-between">
            <span className="text-[9.5px] tracking-[0.22em] uppercase text-compose-inkSoft transition-all duration-500">
              {labels[step]}
            </span>
            <span
              className={`text-[12px] transition-colors duration-500 ${
                step === 3 ? "text-compose-sage" : "text-compose-inkSoft/40"
              }`}
            >
              {step === 3 ? "✓" : "•"}
            </span>
          </div>

          {/* Home indicator */}
          <div className="flex justify-center pb-2 pt-1">
            <span className="w-24 h-1 rounded-full bg-compose-ink/20" />
          </div>
        </div>
      </div>
    </div>
  );
};

const SignupForm = ({ idPrefix = "hero", variant = "light" }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    const value = email.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (!valid) {
      setError("Vul een geldig e-mailadres in");
      return;
    }
    setError("");
    const subject = encodeURIComponent("Inschrijving Compose app");
    const body = encodeURIComponent(
      `Hallo Compose team,\n\nIk wil me graag inschrijven voor de Compose app.\n\nMijn e-mailadres: ${value}\n\nMet vriendelijke groet`
    );
    window.location.href = `mailto:${RECIPIENT}?subject=${subject}&body=${body}`;
  };

  return (
    <form
      onSubmit={onSubmit}
      className="w-full"
      data-testid={`${idPrefix}-signup-form`}
      noValidate
    >
      <div className="flex flex-col sm:flex-row gap-3 w-full">
        <div className="relative flex-1">
          <Mail
            className="absolute left-4 top-1/2 -translate-y-1/2 text-compose-inkSoft/60"
            size={18}
            strokeWidth={1.5}
          />
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="jouw@email.nl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={`${idPrefix}-email-input`}
            className={`w-full pl-11 pr-4 py-3.5 rounded-full bg-white border ${
              error ? "border-red-400" : "border-compose-border"
            } text-compose-ink placeholder:text-compose-inkSoft/50 outline-none focus:border-compose-sage focus:ring-2 focus:ring-compose-sage/20 transition-all font-sans text-[15px]`}
            aria-label="E-mailadres"
          />
        </div>
        <button
          type="submit"
          data-testid={`${idPrefix}-signup-submit`}
          className="group inline-flex items-center justify-center gap-2 bg-compose-ink text-compose-bg rounded-full px-7 py-3.5 hover:bg-compose-sage transition-colors duration-300 font-sans text-[15px] font-medium tracking-wide shadow-soft"
        >
          Schrijf me in
          <ArrowRight size={16} strokeWidth={1.75} className="group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
      {error && (
        <p
          className="mt-2 text-sm text-red-600 font-sans pl-2"
          data-testid={`${idPrefix}-form-error`}
        >
          {error}
        </p>
      )}
      <p className="mt-3 text-xs text-compose-inkSoft/70 font-sans pl-2" data-testid={`${idPrefix}-form-helper`}>
        Klik op inschrijven — je mailprogramma opent met een vooringevuld bericht.
      </p>
    </form>
  );
};

const Logo = ({ className = "" }) => (
  <a href="#top" className={`flex items-center gap-2 ${className}`} data-testid="brand-logo">
    <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-compose-ink text-compose-bg">
      <Leaf size={18} strokeWidth={1.5} />
    </span>
    <span className="font-serif text-[22px] tracking-tight text-compose-ink">Compose</span>
  </a>
);

export default function Landing() {
  const steps = useMemo(
    () => [
      {
        n: "01",
        title: "Veilig importeren — eenzijdig gebruik",
        body:
          "Koppel je eigen e-mailaccount (Gmail, Outlook) veilig aan Compose. De andere partij hoeft niks te doen of te weten. Berichten van de contacten die jij aangeeft worden automatisch geïmporteerd en centraal opgeslagen in een beveiligde omgeving.",
        icon: ShieldCheck,
      },
      {
        n: "02",
        title: "Helder inzicht",
        body:
          "Onze AI analyseert de toon en inhoud van elke e-mail en geeft je een neutrale, heldere samenvatting. Zo zie je in één oogopslag waar de mail over gaat — zonder de emotionele lading.",
        icon: Sparkles,
      },
      {
        n: "03",
        title: "Rustig reageren",
        body:
          "Ontvang voorgestelde antwoordconcepten die neutraal en respectvol zijn opgesteld. Pas ze aan of gebruik ze direct, zodat jij de regie houdt.",
        icon: MessageSquareText,
      },
    ],
    []
  );

  const benefits = [
    { icon: Shield, title: "100% veilig & vertrouwelijk", body: "Geen toegang voor derden, veilige opslag." },
    { icon: HeartHandshake, title: "Geen emotionele triggers", body: "Samenvattingen en antwoorden zijn neutraal en constructief." },
    { icon: Clock, title: "Tijdbesparend", body: "Geen uren meer piekeren over hoe je moet reageren." },
    { icon: Users, title: "Eenzijdig gebruik mogelijk", body: "Geen account of medewerking van de ander nodig. Jij begint vandaag, voor jezelf." },
    { icon: Anchor, title: "Jouw eigen anker", body: "Stel je constructief kader vast — Compose toetst elk antwoord eraan, zodat je daar niet van afdwaalt." },
  ];

  return (
    <div id="top" className="min-h-screen bg-compose-bg text-compose-ink font-sans">
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-compose-bg/70 border-b border-compose-border/40"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-9 text-[14px] text-compose-inkSoft" aria-label="Hoofdnavigatie">
            <a href="#app-preview" className="hover:text-compose-ink transition-colors" data-testid="nav-link-preview">Bekijk de app</a>
            <a href="#hoe-werkt-het" className="hover:text-compose-ink transition-colors" data-testid="nav-link-how">Hoe werkt het</a>
            <a href="#voordelen" className="hover:text-compose-ink transition-colors" data-testid="nav-link-benefits">Voordelen</a>
            <a href="#prijs" className="hover:text-compose-ink transition-colors" data-testid="nav-link-pricing">Prijs</a>
            <a href="#inschrijven" className="hover:text-compose-ink transition-colors" data-testid="nav-link-signup">Inschrijven</a>
          </nav>
          <a
            href="#inschrijven"
            data-testid="header-cta"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium bg-compose-ink text-compose-bg rounded-full px-5 py-2.5 hover:bg-compose-sage transition-colors"
          >
            Inschrijven
            <ArrowRight size={14} strokeWidth={1.75} />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden" data-testid="hero-section">
        {/* Background gradient image */}
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center opacity-70"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1744131290886-8e618de813cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHNvZnQlMjBncmFkaWVudCUyMGJlaWdlJTIwc2FnZXxlbnwwfHx8fDE3Nzg2NzgyMTh8MA&ixlib=rb-4.1.0&q=85')",
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 -z-10 bg-compose-bg/55" aria-hidden="true" />
        <div className="absolute inset-0 -z-10 grain" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-7">
                  <span className="w-6 h-px bg-compose-sage/70" />
                  Voor stressvrije communicatie · eenzijdig te gebruiken
                </span>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="font-serif font-light text-[44px] sm:text-6xl lg:text-[76px] leading-[1.02] tracking-tight text-compose-ink">
                  Berichten en e-mails in een conflictsituatie
                  <span className="block italic text-compose-sage"> voelen als een mijnenveld.</span>
                </h1>
              </Reveal>
              <Reveal delay={180}>
                <p className="mt-7 text-lg sm:text-xl leading-relaxed text-compose-inkSoft max-w-2xl">
                  Elk bericht brengt onzekerheid met zich mee. Bang voor escalatie, misverstanden of dat je per ongeluk
                  iets zegt waar je spijt van krijgt?
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-8 sm:mt-10 relative max-w-2xl">
                  <span className="absolute -left-4 top-1 bottom-1 w-[2px] bg-compose-sage rounded-full" aria-hidden="true" />
                  <p className="text-xl sm:text-2xl leading-relaxed text-compose-ink font-serif font-light pl-2">
                    <span className="text-compose-sage not-italic font-medium font-sans text-[11px] tracking-[0.22em] uppercase block mb-3">
                      De oplossing van Compose
                    </span>
                    Compose zorgt ervoor dat <em className="text-compose-sage">jij</em> die berichten <em className="text-compose-sage">veilig</em> ontvangt,
                    <em className="text-compose-sage"> helder samengevat</em> krijgt en met
                    <em className="text-compose-sage"> rust</em> kunt beantwoorden.
                  </p>
                  <p className="mt-5 text-xl sm:text-2xl leading-relaxed text-compose-ink font-serif font-light pl-2">
                    Stel zelf in wat een <em className="text-compose-sage">constructief kader</em> is en de
                    app houdt alle communicatie daarop gericht.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={280}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-compose-inkSoft">
                  <div className="flex items-center gap-2 text-compose-ink font-medium">
                    <Users size={16} strokeWidth={1.5} className="text-compose-sage" />
                    Eenzijdig gebruik mogelijk
                  </div>
                  <span className="hidden sm:block w-px h-4 bg-compose-border" aria-hidden="true" />
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} strokeWidth={1.5} className="text-compose-sage" />
                    Veilige en centrale opslag
                  </div>
                  <span className="hidden sm:block w-px h-4 bg-compose-border" aria-hidden="true" />
                  <div className="flex items-center gap-2">
                    <Inbox size={16} strokeWidth={1.5} className="text-compose-sage" />
                    Gmail · Outlook
                  </div>
                </div>
              </Reveal>

              <Reveal delay={360}>
                <div className="mt-8 max-w-2xl">
                  <p className="text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-3">
                    Voor wie is Compose?
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="use-cases-chips">
                    {[
                      "Scheidingstrajecten",
                      "Co-ouderschap",
                      "Mediation",
                      "Juridische trajecten",
                      "Lastige werkrelaties",
                    ].map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center text-[13px] text-compose-ink bg-white border border-compose-border/70 rounded-full px-3.5 py-1.5 hover:border-compose-sage transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Visual */}
            <div className="lg:col-span-5">
              <Reveal delay={200}>
                <HeroFlowAnimation />
              </Reveal>
            </div>
          </div>

          {/* Hero signup — onder het voorbeeld */}
          <Reveal delay={120}>
            <div className="mt-16 sm:mt-20 max-w-2xl mx-auto lg:mx-0" id="inschrijven-hero">
              <p className="text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-4">
                Schrijf je in
              </p>
              <SignupForm idPrefix="hero" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT SHOWCASE */}
      <section id="app-preview" className="relative py-24 sm:py-32 bg-compose-surface/40" data-testid="showcase-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="inline-block text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-4">
                  Een blik in Compose
                </span>
                <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
                  Van geladen e-mail
                  <span className="italic text-compose-sage"> naar heldere actie.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={100}>
                <p className="text-lg leading-relaxed text-compose-inkSoft">
                  Zo voelt Compose op je laptop én onderweg op je telefoon — rustig, leesbaar en gemaakt om je
                  ademruimte te geven.
                </p>
                <p className="mt-5 text-sm leading-relaxed text-compose-inkSoft italic border-l-2 border-compose-sage/50 pl-4">
                  Voorbeeld uit een scheidingstraject: een geladen e-mail van je ex over de vakantieplanning wordt
                  door Compose vertaald in een neutrale samenvatting met een rustig conceptantwoord — zodat je niet
                  vanuit emotie hoeft te reageren.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Laptop / desktop mock */}
            <Reveal className="lg:col-span-8">
              <div className="relative bg-white border border-compose-border/60 rounded-[2rem] p-3 sm:p-4 shadow-soft" data-testid="desktop-mockup">
                {/* Browser chrome */}
                <div className="flex items-center justify-between px-3 py-2 border-b border-compose-border/60">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-compose-terracotta/70" />
                    <span className="w-2.5 h-2.5 rounded-full bg-compose-dusty/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-compose-sage/70" />
                  </div>
                  <div className="hidden sm:flex items-center gap-2 text-[11px] text-compose-inkSoft bg-compose-bg/80 rounded-full px-3 py-1 border border-compose-border/50">
                    <span className="w-1.5 h-1.5 rounded-full bg-compose-sage" />
                    app.compose.nl / inbox
                  </div>
                  <span className="text-[10px] tracking-[0.18em] uppercase text-compose-inkSoft hidden sm:block">Compose</span>
                </div>

                {/* Inbox layout */}
                <div className="grid grid-cols-12 gap-0 min-h-[420px]">
                  {/* Sidebar */}
                  <aside className="col-span-3 border-r border-compose-border/60 p-4 bg-compose-bg/40 hidden sm:block">
                    <div className="flex items-center gap-2 mb-5">
                      <span className="w-7 h-7 rounded-full bg-compose-ink text-compose-bg flex items-center justify-center">
                        <Leaf size={14} strokeWidth={1.5} />
                      </span>
                      <span className="font-serif text-base text-compose-ink">Compose</span>
                    </div>
                    <ul className="space-y-1.5 text-[12.5px]">
                      <li className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-compose-sage/15 text-compose-ink font-medium">
                        <Inbox size={14} strokeWidth={1.5} /> Inbox <span className="ml-auto text-[10px] bg-compose-ink text-compose-bg rounded-full px-1.5">3</span>
                      </li>
                      <li className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-compose-inkSoft hover:bg-compose-surface/60">
                        <Sparkles size={14} strokeWidth={1.5} /> Samenvattingen
                      </li>
                      <li className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-compose-inkSoft hover:bg-compose-surface/60">
                        <MessageSquareText size={14} strokeWidth={1.5} /> Concepten
                      </li>
                      <li className="flex items-center gap-2 px-2.5 py-2 rounded-lg text-compose-inkSoft hover:bg-compose-surface/60">
                        <ShieldCheck size={14} strokeWidth={1.5} /> Veilige opslag
                      </li>
                    </ul>
                    <div className="mt-6 pt-5 border-t border-compose-border/60">
                      <p className="text-[10px] uppercase tracking-[0.18em] text-compose-inkSoft mb-2">Contacten</p>
                      <ul className="space-y-2 text-[12px] text-compose-inkSoft">
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-compose-terracotta/30 text-compose-terracotta flex items-center justify-center text-[10px] font-semibold">M</span>
                          Mark
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-compose-dusty/30 text-compose-dusty flex items-center justify-center text-[10px] font-semibold">A</span>
                          Advocaat
                        </li>
                      </ul>
                    </div>
                  </aside>

                  {/* Email list */}
                  <div className="col-span-12 sm:col-span-4 border-r border-compose-border/60">
                    <div className="px-4 py-3 border-b border-compose-border/60 flex items-center justify-between">
                      <p className="text-[13px] font-medium text-compose-ink">Inbox</p>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-compose-sage">Nieuw</span>
                    </div>
                    <ul className="divide-y divide-compose-border/60">
                      {[
                        { from: "Mark", subject: "Schoolvakantie planning", preview: "Ik wil dit echt vandaag nog…", color: "terracotta", active: true },
                        { from: "Advocaat", subject: "Update mediation traject", preview: "Bijgaand de concept-afspraken voor…", color: "dusty" },
                        { from: "Mark", subject: "Re: ophalen zaterdag", preview: "Dit kan zo niet doorgaan, ik vind…", color: "terracotta" },
                      ].map((m, i) => (
                        <li key={i} className={`px-4 py-3.5 cursor-pointer ${m.active ? "bg-compose-sage/10" : "hover:bg-compose-surface/50"}`}>
                          <div className="flex items-center gap-2.5">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                              m.color === "terracotta" ? "bg-compose-terracotta/25 text-compose-terracotta" : "bg-compose-dusty/25 text-compose-dusty"
                            }`}>{m.from[0]}</span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[12.5px] font-medium text-compose-ink truncate">{m.from}</p>
                                <span className="text-[10px] text-compose-inkSoft shrink-0">{i === 0 ? "nu" : i === 1 ? "gist." : "ma"}</span>
                              </div>
                              <p className="text-[12.5px] text-compose-ink truncate">{m.subject}</p>
                              <p className="text-[11.5px] text-compose-inkSoft truncate">{m.preview}</p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Detail */}
                  <div className="col-span-12 sm:col-span-5 p-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-[11px] tracking-[0.18em] uppercase text-compose-inkSoft">Van Mark · vandaag 09:12</p>
                        <h4 className="font-serif text-xl text-compose-ink mt-1">Schoolvakantie planning</h4>
                      </div>
                      <span className="text-[10px] tracking-[0.18em] uppercase text-compose-terracotta bg-compose-terracotta/10 rounded-full px-2.5 py-1">Geladen</span>
                    </div>

                    <div className="rounded-2xl bg-compose-bg/60 border border-compose-border/60 p-4 mb-4">
                      <p className="text-[12.5px] leading-relaxed text-compose-inkSoft italic">
                        &ldquo;Ik snap niet waarom je dit altijd zo moeilijk maakt. Als jij niet meewerkt aan de vakantieweken,
                        regel ik het zelf wel — en dan zien we wel verder…&rdquo;
                      </p>
                    </div>

                    <div className="rounded-2xl bg-white border border-compose-sage/30 p-4 mb-3 shadow-card">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={14} strokeWidth={1.5} className="text-compose-sage" />
                        <span className="text-[10px] tracking-[0.18em] uppercase text-compose-sage font-medium">Samenvatting</span>
                      </div>
                      <p className="text-[12.5px] leading-relaxed text-compose-ink">
                        Mark wil de vakantieweken bespreken en stelt voor om dat snel te doen. Hij vraagt om jouw input.
                      </p>
                    </div>

                    <div className="rounded-2xl bg-compose-sage/8 border border-compose-sage/30 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquareText size={14} strokeWidth={1.5} className="text-compose-sage" />
                        <span className="text-[10px] tracking-[0.18em] uppercase text-compose-sage font-medium">Voorgesteld antwoord</span>
                      </div>
                      <p className="text-[12.5px] leading-relaxed text-compose-ink">
                        Dank je voor je bericht. Laten we deze week even afstemmen over de vakantieweken — ik plan graag
                        een kort moment in om samen tot een goede verdeling te komen.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Phone mock */}
            <Reveal delay={140} className="lg:col-span-4">
              <div className="relative mx-auto max-w-[300px] lg:max-w-none" data-testid="phone-mockup">
                <div className="relative bg-compose-ink rounded-[2.5rem] p-3 shadow-glow">
                  {/* Notch */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-compose-ink rounded-b-2xl z-10 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-compose-inkSoft/40" />
                  </div>
                  <div className="bg-compose-bg rounded-[2rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-6 pt-6 pb-3 text-[10px] font-medium text-compose-ink">
                      <span>9:41</span>
                      <div className="flex items-center gap-1 opacity-70">
                        <span className="w-1 h-1 rounded-full bg-compose-ink" />
                        <span className="w-1 h-1 rounded-full bg-compose-ink" />
                        <span className="w-1 h-1 rounded-full bg-compose-ink" />
                      </div>
                    </div>

                    {/* Header */}
                    <div className="px-5 pt-2 pb-4 flex items-center justify-between">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] text-compose-inkSoft">Inbox</p>
                        <p className="font-serif text-[22px] text-compose-ink leading-tight mt-0.5">Goedemorgen</p>
                      </div>
                      <span className="w-9 h-9 rounded-full bg-compose-ink text-compose-bg flex items-center justify-center">
                        <Leaf size={16} strokeWidth={1.5} />
                      </span>
                    </div>

                    {/* Cards */}
                    <div className="px-4 pb-6 space-y-3">
                      <div className="rounded-2xl bg-white border border-compose-border/70 p-4 shadow-card">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-compose-terracotta/25 text-compose-terracotta text-[10px] font-semibold flex items-center justify-center">M</span>
                            <p className="text-[11.5px] font-medium text-compose-ink">Mark</p>
                          </div>
                          <span className="text-[9px] tracking-[0.18em] uppercase text-compose-terracotta">Geladen</span>
                        </div>
                        <p className="text-[11.5px] text-compose-ink mb-1.5 font-medium">Schoolvakantie planning</p>
                        <p className="text-[11px] leading-relaxed text-compose-inkSoft">
                          Samenvatting: voorstel om vakantieweken te bespreken en samen tot een verdeling te komen.
                        </p>
                      </div>

                      <div className="rounded-2xl bg-compose-sage/10 border border-compose-sage/30 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquareText size={12} strokeWidth={1.5} className="text-compose-sage" />
                          <span className="text-[9px] tracking-[0.18em] uppercase text-compose-sage font-medium">Concept</span>
                        </div>
                        <p className="text-[11px] leading-relaxed text-compose-ink">
                          Dank je voor je bericht. Laten we deze week even afstemmen om samen tot een goede verdeling
                          te komen.
                        </p>
                        <div className="mt-3 flex gap-2">
                          <span className="text-[10px] px-3 py-1 rounded-full bg-compose-ink text-compose-bg font-medium">Verstuur</span>
                          <span className="text-[10px] px-3 py-1 rounded-full border border-compose-border text-compose-ink">Pas aan</span>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-white border border-compose-border/70 p-3 flex items-center gap-2.5">
                        <span className="w-6 h-6 rounded-full bg-compose-dusty/25 text-compose-dusty text-[10px] font-semibold flex items-center justify-center">A</span>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] font-medium text-compose-ink truncate">Advocaat · Update traject</p>
                          <p className="text-[10.5px] text-compose-inkSoft truncate">Bijgaand de concept-afspraken…</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="hoe-werkt-het" className="relative py-24 sm:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-16 lg:mb-20 items-end">
            <div className="lg:col-span-6">
              <Reveal>
                <span className="inline-block text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-4">
                  Hoe werkt het
                </span>
                <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
                  Drie rustige stappen <span className="italic text-compose-sage">naar helderheid</span>.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <Reveal delay={100}>
                <p className="text-lg leading-relaxed text-compose-inkSoft">
                  Compose werkt op de achtergrond — zo blijf jij in control. Geen ingewikkelde dashboards, geen meldingen
                  die je op scherp zetten. Alleen wat je nodig hebt, op het moment dat je er klaar voor bent.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Bento steps */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const span =
                i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-12 lg:col-span-12";
              return (
                <Reveal key={s.n} delay={i * 120} className={span}>
                  <article
                    data-testid={`step-card-${i + 1}`}
                    className="group h-full bg-white border border-compose-border/60 rounded-3xl p-8 sm:p-10 shadow-card hover:-translate-y-1 transition-transform duration-500"
                  >
                    <div className="flex items-start justify-between mb-8">
                      <span className="font-serif text-[44px] leading-none text-compose-sage/70 font-light">
                        {s.n}
                      </span>
                      <span className="w-11 h-11 rounded-full bg-compose-surface flex items-center justify-center text-compose-ink group-hover:bg-compose-sage group-hover:text-white transition-colors">
                        <Icon size={20} strokeWidth={1.4} />
                      </span>
                    </div>
                    <h3 className="font-serif text-2xl sm:text-[28px] font-normal text-compose-ink mb-3 tracking-tight">
                      {s.title}
                    </h3>
                    <p className="text-[15px] sm:text-base leading-relaxed text-compose-inkSoft max-w-xl">{s.body}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ANKER — sub-feature */}
      <section id="anker" className="relative py-16 sm:py-20" data-testid="anker-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <article className="grid lg:grid-cols-12 gap-10 items-center bg-compose-surface/60 border border-compose-border/60 rounded-[2.5rem] p-8 sm:p-10 lg:p-14">
              <div className="lg:col-span-7">
                <div className="flex items-center gap-2 mb-5">
                  <span className="w-9 h-9 rounded-full bg-compose-ink text-compose-bg flex items-center justify-center">
                    <Anchor size={17} strokeWidth={1.5} />
                  </span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold">
                    Een van de features
                  </span>
                </div>
                <h2 className="font-serif font-light text-3xl sm:text-4xl leading-[1.1] tracking-tight text-compose-ink mb-4">
                  Jouw <span className="italic text-compose-sage">Anker</span> — een rustig kompas in elk gesprek.
                </h2>
                <p className="text-base sm:text-lg leading-relaxed text-compose-inkSoft mb-4">
                  Bepaal vooraf jouw <em className="not-italic text-compose-ink font-medium">constructief kader</em>:
                  wat is je gezamenlijke doel, welke toon hoort daarbij, wat wil je vermijden? Compose toetst elk
                  voorgesteld antwoord aan jouw anker — zodat geen enkele reactie je daarvan wegtrekt.
                </p>
                <p className="text-[13.5px] text-compose-inkSoft">
                  Stel het één keer in, pas het aan wanneer dat nodig is. Jij houdt de regie.
                </p>
              </div>

              {/* Mock visual */}
              <div className="lg:col-span-5">
                <div className="bg-white border border-compose-border/60 rounded-2xl p-5 shadow-card" data-testid="anker-mock">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Anchor size={14} strokeWidth={1.5} className="text-compose-sage" />
                      <p className="text-[11px] tracking-[0.18em] uppercase text-compose-sage font-medium">Jouw Anker</p>
                    </div>
                    <span className="text-[10px] text-compose-inkSoft">Actief</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.18em] text-compose-inkSoft mb-1">Doel</p>
                      <p className="text-[13px] text-compose-ink leading-snug">
                        Een soepele overdracht rondom de kinderen.
                      </p>
                    </div>
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.18em] text-compose-inkSoft mb-1">Toon</p>
                      <div className="flex flex-wrap gap-1.5">
                        {["Respectvol", "Zakelijk", "Kort"].map((t) => (
                          <span key={t} className="text-[11px] text-compose-ink bg-compose-sage/15 rounded-full px-2.5 py-0.5">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.18em] text-compose-inkSoft mb-1">Vermijd</p>
                      <div className="flex flex-wrap gap-1.5">
                        {["Verwijten", "Oude conflicten"].map((t) => (
                          <span key={t} className="text-[11px] text-compose-ink bg-compose-terracotta/10 rounded-full px-2.5 py-0.5">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-compose-border/60 flex items-center gap-2 text-[11.5px] text-compose-sage">
                    <Check size={13} strokeWidth={2} />
                    Antwoord past binnen jouw anker
                  </div>
                </div>
              </div>
            </article>
          </Reveal>
        </div>
      </section>

      {/* QUOTE / TRANSITION */}
      <section className="relative py-20 sm:py-28" data-testid="quote-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <div className="relative rounded-[2.5rem] overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('https://images.unsplash.com/photo-1767234753552-3c71aebf2506?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxjYWxtJTIwc2VyZW5lJTIwcGVyc29uJTIwbmF0dXJlfGVufDB8fHx8MTc3ODY3ODIxNHww&ixlib=rb-4.1.0&q=85')",
                }}
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-compose-ink/40" aria-hidden="true" />
              <div className="relative px-8 py-16 sm:p-20 lg:p-24 text-compose-bg">
                <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight font-light tracking-tight max-w-3xl">
                  “Eindelijk een inbox die niet aan mijn zenuwen trekt. Compose vertaalt de chaos in iets waar ik weer
                  ademruimte bij vind.”
                </p>
                <div className="mt-8 flex items-center gap-3 text-sm tracking-wide uppercase text-compose-bg/80">
                  <span className="w-8 h-px bg-compose-bg/60" />
                  Voor iedereen in moeilijke gesprekken
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="voordelen" className="relative py-24 sm:py-32 bg-compose-surface/50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start mb-14">
            <div className="lg:col-span-5">
              <Reveal>
                <span className="inline-block text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-4">
                  Voordelen
                </span>
                <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-compose-ink">
                  Gemaakt om je <span className="italic text-compose-terracotta">rust</span> terug te geven.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={100}>
                <p className="text-lg leading-relaxed text-compose-inkSoft">
                  Geen tooltjes of trucs. Compose is een doordachte ruimte om e-mails en berichten te verwerken, ontworpen vanuit
                  empathie voor mensen in lastige gesprekken — denk co-ouderschap, mediation of juridische trajecten.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 80}>
                  <article
                    data-testid={`benefit-card-${i + 1}`}
                    className="h-full bg-white border border-compose-border/60 rounded-3xl p-8 shadow-card hover:-translate-y-1 transition-transform duration-500 flex gap-5"
                  >
                    <div className="shrink-0 w-12 h-12 rounded-2xl bg-compose-bg flex items-center justify-center text-compose-sage">
                      <Icon size={22} strokeWidth={1.4} />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl text-compose-ink tracking-tight mb-2">{b.title}</h3>
                      <p className="text-[15px] leading-relaxed text-compose-inkSoft">{b.body}</p>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="prijs" className="relative py-24 sm:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <Reveal>
                <span className="inline-block text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-4">
                  Prijs
                </span>
                <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-tight">
                  Eerlijk geprijsd,
                  <span className="italic text-compose-terracotta"> beloond als je er vroeg bij bent.</span>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <Reveal delay={100}>
                <p className="text-lg leading-relaxed text-compose-inkSoft">
                  Eén heldere prijs, geen verborgen kosten. De eerste 25 inschrijvingen krijgen het eerste jaar een
                  flinke korting als dank voor hun vertrouwen.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Early bird */}
            <Reveal className="lg:col-span-7">
              <article
                data-testid="pricing-card-earlybird"
                className="relative h-full bg-compose-ink text-compose-bg rounded-3xl p-8 sm:p-10 lg:p-12 overflow-hidden shadow-glow"
              >
                {/* Decorative gradient */}
                <div className="absolute -top-24 -right-20 w-80 h-80 rounded-full bg-compose-terracotta/30 blur-3xl pointer-events-none" aria-hidden="true" />
                <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-compose-sage/20 blur-3xl pointer-events-none" aria-hidden="true" />

                <div className="relative">
                  <div className="flex flex-wrap items-center gap-3 mb-7">
                    <span className="inline-flex items-center gap-1.5 bg-compose-terracotta text-compose-bg rounded-full px-3 py-1 text-[11px] tracking-[0.18em] uppercase font-medium">
                      <Flame size={12} strokeWidth={1.75} />
                      Early bird · max. 25 plekken
                    </span>
                    <span className="text-[11px] tracking-[0.18em] uppercase text-compose-bg/60">
                      Alleen voor de eerste inschrijvingen
                    </span>
                  </div>

                  <h3 className="font-serif text-3xl sm:text-4xl font-light leading-tight mb-3">
                    Compose <span className="italic">Founders</span>
                  </h3>
                  <p className="text-compose-bg/70 max-w-md mb-9 leading-relaxed">
                    Wees een van de eersten die rust in hun inbox terugkrijgt — en betaal het hele eerste jaar een
                    speciale tarief.
                  </p>

                  <div className="flex items-end gap-4 mb-2">
                    <div className="flex items-baseline">
                      <span className="font-serif text-6xl sm:text-7xl font-light tracking-tight">€9,99</span>
                      <span className="ml-2 text-compose-bg/70 text-base">/ maand</span>
                    </div>
                  </div>
                  <p className="text-sm text-compose-bg/60 mb-9">
                    <span className="line-through decoration-compose-terracotta/70">€19,99 / maand</span>
                    <span className="ml-3 inline-flex items-center gap-1.5 text-compose-terracotta">
                      <span className="w-1.5 h-1.5 rounded-full bg-compose-terracotta" />
                      50% korting · je bespaart €120 in het eerste jaar
                    </span>
                  </p>

                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 mb-10">
                    {[
                      "Volledige toegang vanaf lancering",
                      "Veilig importeren (Gmail · Outlook)",
                      "AI samenvattingen & antwoorden",
                      "Veilige en centrale opslag",
                      "Persoonlijke onboarding",
                      "Eerste jaar gegarandeerd €9,99",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-[14.5px] text-compose-bg/90">
                        <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-compose-sage/30 text-compose-bg flex items-center justify-center">
                          <Check size={12} strokeWidth={2} />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <a
                    href="#inschrijven"
                    data-testid="pricing-earlybird-cta"
                    className="group inline-flex items-center justify-center gap-2 bg-compose-bg text-compose-ink rounded-full px-7 py-3.5 hover:bg-compose-terracotta hover:text-compose-bg transition-colors duration-300 font-sans text-[15px] font-medium tracking-wide"
                  >
                    Claim mijn plek
                    <ArrowRight size={16} strokeWidth={1.75} className="group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </article>
            </Reveal>

            {/* Standard */}
            <Reveal delay={140} className="lg:col-span-5">
              <article
                data-testid="pricing-card-standard"
                className="relative h-full bg-white border border-compose-border/60 rounded-3xl p-8 sm:p-10 shadow-card flex flex-col"
              >
                <div className="mb-7">
                  <span className="inline-flex items-center gap-1.5 bg-compose-surface text-compose-inkSoft rounded-full px-3 py-1 text-[11px] tracking-[0.18em] uppercase font-medium">
                    Standaard tarief
                  </span>
                </div>

                <h3 className="font-serif text-3xl sm:text-4xl font-light leading-tight mb-3 text-compose-ink">
                  Compose
                </h3>
                <p className="text-compose-inkSoft max-w-sm mb-8 leading-relaxed">
                  Na de eerste 25 inschrijvingen geldt het reguliere maandtarief.
                </p>

                <div className="flex items-baseline mb-1">
                  <span className="font-serif text-5xl sm:text-6xl font-light tracking-tight text-compose-ink">€19,99</span>
                  <span className="ml-2 text-compose-inkSoft text-base">/ maand</span>
                </div>
                <p className="text-sm text-compose-inkSoft mb-9">Maandelijks opzegbaar.</p>

                <ul className="space-y-3 mb-8">
                  {[
                    "Veilig importeren van berichten",
                    "AI samenvattingen & antwoorden",
                    "Veilige en centrale opslag",
                    "E-mail support",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[14.5px] text-compose-ink">
                      <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-compose-sage/15 text-compose-sage flex items-center justify-center">
                        <Check size={12} strokeWidth={2} />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <a
                    href="#inschrijven"
                    data-testid="pricing-standard-cta"
                    className="inline-flex items-center gap-2 text-compose-ink underline underline-offset-4 decoration-compose-sage hover:decoration-compose-terracotta transition-colors font-sans text-[15px] font-medium"
                  >
                    Inschrijven voor lancering
                    <ArrowRight size={16} strokeWidth={1.75} />
                  </a>
                </div>
              </article>
            </Reveal>
          </div>

          <Reveal delay={200}>
            <p className="mt-8 text-sm text-compose-inkSoft text-center" data-testid="pricing-disclaimer">
              Prijzen zijn inclusief btw. De early-bird-korting geldt 12 maanden vanaf lancering en is gelimiteerd tot
              de eerste 25 inschrijvingen.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="inschrijven" className="relative py-28 sm:py-36" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <span className="inline-block text-[11px] tracking-[0.22em] uppercase text-compose-sage font-semibold mb-5">
              Klaar voor stressvrije communicatie?
            </span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-[60px] leading-[1.05] tracking-tight text-compose-ink">
              Schrijf je nu in voor <span className="italic">Compose</span>.
            </h2>
            <p className="mt-6 text-lg text-compose-inkSoft max-w-xl mx-auto">
              Wij houden je op de hoogte van de lancering. Geen spam, alleen een rustig bericht zodra Compose voor jou
              klaarstaat.
            </p>
          </Reveal>
          <Reveal delay={140}>
            <div className="mt-10 max-w-xl mx-auto text-left">
              <SignupForm idPrefix="footer" />
            </div>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-10 text-sm text-compose-inkSoft">
              Of mail ons direct:{" "}
              <a
                href={`mailto:${RECIPIENT}?subject=${encodeURIComponent("Inschrijving Compose app")}`}
                className="text-compose-ink underline underline-offset-4 decoration-compose-sage hover:decoration-compose-terracotta transition-colors"
                data-testid="direct-mailto-link"
              >
                {RECIPIENT}
              </a>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-compose-border/60 py-12" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-col sm:flex-row justify-between items-center gap-6">
          <Logo />
          <p className="text-sm text-compose-inkSoft">
            © {new Date().getFullYear()} Compose · Voor rust in elk gesprek.
          </p>
          <a
            href="#top"
            className="text-sm text-compose-inkSoft hover:text-compose-ink transition-colors inline-flex items-center gap-1"
            data-testid="back-to-top"
          >
            Terug naar boven <ChevronDown size={14} className="rotate-180" strokeWidth={1.5} />
          </a>
        </div>
      </footer>
    </div>
  );
}
